import React, { Suspense, useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import useAuth from '../hooks/useAuth';
import navigationRef from '../utils/navigation';
import { flattenMenus } from '../utils/menuRouting';
import componentRegistry from './componentRegistry';
import lazyWithRetry from '../utils/lazyWithRetry';
import ErrorBoundary from '../components/common/ErrorBoundary';

// Shell layouts
import AppShell from '../components/layout/AppShell';
import AuthShell from '../components/layout/AuthShell';

// Route guards
import ProtectedRoute from './ProtectedRoute';
import PublicRoute from './PublicRoute';

// Views
const LoginPage = lazyWithRetry(() => import('../pages/LoginPage'));
const RegisterPage = lazyWithRetry(() => import('../pages/RegisterPage'));
const ForgotPasswordPage = lazyWithRetry(() => import('../pages/ForgotPasswordPage'));
const NotFoundPage = lazyWithRetry(() => import('../pages/NotFoundPage'));

// Admin Views
const DashboardPage = lazyWithRetry(() => import('../pages/DashboardPage'));
const CallBackRequestsPage = lazyWithRetry(() => import('../pages/CallBackRequestsPage'));
const AccountRequestsPage = lazyWithRetry(() => import('../pages/AccountRequestsPage'));
const ContactEnquiriesPage = lazyWithRetry(() => import('../pages/ContactEnquiriesPage'));
const FranchiseApplicationsPage = lazyWithRetry(() => import('../pages/FranchiseApplicationsPage'));
const JobSeekersPage = lazyWithRetry(() => import('../pages/JobSeekersPage'));
const ManageDownloadsPage = lazyWithRetry(() => import('../pages/ManageDownloadsPage'));
const ResearchManagerPage = lazyWithRetry(() => import('../pages/ResearchManagerPage'));
const InvestorsManagerPage = lazyWithRetry(() => import('../pages/InvestorsManagerPage'));
const StatutoryDocumentsPage = lazyWithRetry(() => import('../pages/StatutoryDocumentsPage'));
const SebiComplaintsManagerPage = lazyWithRetry(() => import('../pages/SebiComplaintsManagerPage'));
const DpComplaintsManagerPage = lazyWithRetry(() => import('../pages/DpComplaintsManagerPage'));
const MessageNotificationPage = lazyWithRetry(() => import('../pages/MessageNotificationPage'));
const TestimonialsPage = lazyWithRetry(() => import('../pages/TestimonialsPage'));
const WebsitePopupPage = lazyWithRetry(() => import('../pages/WebsitePopupPage'));

export const AppRoutes = () => {
  const navigate = useNavigate();

  // Initialize navigation ref for Axios interceptors
  useEffect(() => {
    navigationRef.setNavigate(navigate);
  }, [navigate]);

  return (
    <ErrorBoundary>
      <Suspense
        fallback={
          <div className="flex h-screen w-screen items-center justify-center bg-slate-50 dark:bg-darkbg-base theme-transition">
            <div className="flex flex-col items-center gap-3">
              <div className="h-12 w-12 animate-spin rounded-full border-4 border-slate-200 border-t-primary-500"></div>
              <span className="text-sm font-medium text-slate-500 dark:text-slate-400">Loading view...</span>
            </div>
          </div>
        }
      >
        <Routes>
          {/* Public auth paths wrapped in AuthShell and PublicRoute */}
          <Route element={<PublicRoute><AuthShell /></PublicRoute>}>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          </Route>

          {/* Protected dashboard paths wrapped in AppShell and ProtectedRoute */}
          <Route element={<ProtectedRoute><AppShell /></ProtectedRoute>}>
            {/* Default redirect to view-callback (first item in legacy admin) */}
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="/dashboard" element={<DashboardPage />} />
            
            {/* Static Routes matching Sidebar */}
            <Route path="/view-callback" element={<CallBackRequestsPage />} />
            <Route path="/view-open-account" element={<AccountRequestsPage />} />
            <Route path="/add-research" element={<ResearchManagerPage />} />
            <Route path="/view-partner-us" element={<FranchiseApplicationsPage />} />
            <Route path="/view-contact-us" element={<ContactEnquiriesPage />} />
            <Route path="/message-notification" element={<MessageNotificationPage />} />
            <Route path="/testimonials" element={<TestimonialsPage />} />
            <Route path="/website-popup" element={<WebsitePopupPage />} />
            
            {/* Downloads catalog tabs */}
            <Route path="/add-downloads" element={<ManageDownloadsPage key="add-downloads" defaultTab="catalog" />} />
            <Route path="/add-subsections" element={<ManageDownloadsPage key="add-subsections" defaultTab="subsections" />} />
            
            {/* Investors folders tabs */}
            <Route path="/investor-disclosures" element={<InvestorsManagerPage key="investor-disclosures" defaultTab="files" />} />
            <Route path="/statutory-documents" element={<StatutoryDocumentsPage />} />
            <Route path="/sebi-complaints-trends" element={<SebiComplaintsManagerPage />} />
            <Route path="/dp-complaints-trends" element={<DpComplaintsManagerPage />} />
            
            {/* Job Applications route (can be accessed via link/URL) */}
            <Route path="/job-applications" element={<JobSeekersPage />} />
          </Route>

          {/* 404 Route */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
    </ErrorBoundary>
  );
};

export default AppRoutes;
